'use client'

import { useHeaderHeight } from 'hooks/useHeaderHeight/useHeaderHeight'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { usePlpContext } from 'plp/context'
import { getTagsData } from 'plp/hooks/useFilters/tags'
import { useScreenSize } from 'plp/hooks/useScreenSize'
import { getServerFiltersData } from 'plp/services/getServerFiltersData'
import type { CatalogGroup, FiltersSearchParams } from 'plp/types/Catalog'
import type { Tag as TagType } from 'plp/types/Filters'
import { EventName } from 'plp/types/eventsObserver'
import { eventsObserver } from 'plp/utils/eventsObserver'
import { type CSSProperties, useCallback, useEffect, useState } from 'react'

import { Columns } from '../Columns/Columns'
import { Tags } from '../Filters/Tags/Tags'
import {
	type UseFiltersData,
	getFiltersData,
} from '../getFiltersData/getFiltersData'
import { FiltersWrapper } from './FiltersWrapper/FilterWrapper'

import styles from './StickyBar.module.scss'

export interface StickyBar {
	defaultFilter?: FiltersSearchParams
	catalogGroups: CatalogGroup[]
	filtersData: UseFiltersData | null
	hasServerFilters: boolean
	shouldRenderFilters: boolean
}

export function StickyBar({
	filtersData,
	catalogGroups,
	defaultFilter,
	hasServerFilters,
	shouldRenderFilters,
}: StickyBar): React.ReactNode {
	const [clientFiltersData, setClientFiltersData] =
		useState<UseFiltersData | null>(filtersData)
	const { t } = useLabels()
	const { country: countryConfig } = useMasterData()
	const { catalogId, channel, country, language } = usePlpContext()
	const { headerHeight } = useHeaderHeight()
	const { customDate } = useMasterData()
	const { showAsMediumOrSmall } = useScreenSize()

	const [show, setShow] = useState(false)

	const onFilterClick = useCallback(async (): Promise<void> => {
		if (!clientFiltersData) {
			const catalogFiltersData = await getServerFiltersData({
				channel,
				country,
				language,
				catalogId,
				customDate,
				isClient: true,
			})
			const currentFiltersData = getFiltersData({
				serverData: catalogFiltersData,
				catalogGroups: catalogGroups ?? [],
			})
			setClientFiltersData(() => currentFiltersData)
		}
		setShow((prev) => !prev)

		eventsObserver.dispatch(EventName.OPEN_FILTERS)
	}, [clientFiltersData])

	const onClose = useCallback(() => {
		setShow(false)
	}, [])

	const tagsData: TagType[] =
		defaultFilter && clientFiltersData
			? getTagsData(defaultFilter, clientFiltersData, countryConfig, t)
			: []

	useEffect(() => {
		if (filtersData) {
			setClientFiltersData(filtersData)
		}
	}, [filtersData])

	return (
		<div
			className={styles.sticky}
			style={
				{
					'--inset-block-start': `${headerHeight}px`,
				} as CSSProperties
			}
		>
			<div className={styles.inner}>
				{shouldRenderFilters ? (
					<FiltersWrapper
						showAsMediumOrSmall={showAsMediumOrSmall}
						show={show}
						clientFiltersData={clientFiltersData}
						hasServerFilters={hasServerFilters}
						defaultFilter={defaultFilter}
						onFilterClick={onFilterClick}
						onClose={onClose}
					/>
				) : (
					<div className={styles.fill} aria-hidden />
				)}
				{!showAsMediumOrSmall && shouldRenderFilters && (
					<Tags tags={tagsData} defaultFilter={defaultFilter} />
				)}
				<Columns
					isSmall={showAsMediumOrSmall}
					hasServerFilters={hasServerFilters}
				/>
			</div>
		</div>
	)
}
